import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  FormControlLabel,
  Button,
  Badge,
  Radio,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { FaArrowLeft, FaEye, FaEyeSlash, FaPlus, FaSearch, FaTrash, FaCloudUploadAlt, FaCogs, FaEdit, FaCloudDownloadAlt, FaCheck, FaTimes, FaFileExport } from 'react-icons/fa';
import { skillsApi, competencyTemplatesApi, organizationApi } from '../../../services/api';
import { useAlert } from '../../../contexts/AlertContext';
import ConfirmationModal from '../../../components/common/ConfirmationModal';
import Modal, { Field, Label } from '../../../components/common/Modal';
import { Container, Header, HeaderLeft, Title, Content, BackButton, HeaderButton, ControlsContainer } from '../../common/styles';
import { MainContainer, MatrixGrid, MatrixCard, MatrixCardContent, MatrixTitle, MatrixDetailsGrid, ItalicText, ActionButtons } from './styles';
import EmptyState from '../../common/EmptyState';
import LoadingSpinner from '../../common/LoadingSpinner';
import { MATRIX_TEMPLATE_STATUS, getStatusColor, getStatusTextColor, getStatusText } from '../../../constants/matrixTemplateStatus';
import { COLORS } from '../../../constants/radar';
import { MATRIX_TEMPLATE_TYPES, getTemplateTypeColor, getTemplateTypeTextColor, getTemplateTypeText } from '../../../constants/matrixTemplateTypes';
import { SKILL_TYPES, getStatusColor as getSkillTypeColor, getStatusTextColor as getSkillTypeTextColor, getStatusText as getSkillTypeText } from '../../../constants/skillTypes';
import { getClusterChipStyle } from '../../../utils/clusterUtils';
import ClusterSelector from '../../common/ClusterSelector';
import { toLocalDate } from '../../../utils/dateOnly';

const SkillsMatrixVersions = ({ 
  // Selection mode props
  isSelectionMode = false,
  onSelectionConfirm = null,
  selectionTitle = null,
  selectionBackPath = null,
  selectionBackText = null,
  sessionId = null,
  initialSelectedVersions = []
}) => {
  const { matrixId } = useParams();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [showControls, setShowControls] = useState(true);
  const [versions, setVersions] = useState([]);
  const [matrix, setMatrix] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [competencyTemplates, setCompetencyTemplates] = useState([]);
  const [templateVersions, setTemplateVersions] = useState([]);
  const [clusters, setClusters] = useState([]);

  // Selection mode state
  const [selectedVersions, setSelectedVersions] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);

  const [versionModal, setVersionModal] = useState({
    isOpen: false,
    mode: 'create', // 'create' or 'edit'
    loading: false,
    version: null, // For edit mode
    formData: {
      description: '',
      weight: '',
      type: '',
      competencyTemplateId: '',
      templateVersionId: '',
      clusterIds: []
    }
  });
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    version: null
  });
  const [publishModal, setPublishModal] = useState({
    open: false,
    version: null
  });
  const [unpublishModal, setUnpublishModal] = useState({
    open: false,
    version: null
  });
  const [exportModal, setExportModal] = useState({
    open: false,
    version: null,
    loading: false,
    submitting: false,
    selectedTemplateId: null,
    allMatrices: []
  });

  useEffect(() => {
    fetchData();
    if (!isSelectionMode) {
      fetchCompetencyTemplates();
      fetchTemplateVersions();
    }
    fetchClusters();
  }, [matrixId, isSelectionMode]);

  // Initialize selected versions from props
  useEffect(() => {
    if (isSelectionMode && initialSelectedVersions.length > 0) {
      const initialSet = new Set(initialSelectedVersions);
      setSelectedVersions(initialSet);
    }
  }, [isSelectionMode, initialSelectedVersions]);

  useEffect(() => {
    if (versionModal.isOpen && versionModal.mode === 'edit' && versionModal.version) {
      // Set loading state
      setVersionModal(prev => ({ ...prev, loading: true }));
      
      // Fetch the version data directly from the API
      const fetchVersionData = async () => {
        try {
          const versionData = await skillsApi.getTemplateById(versionModal.version.id);
          
          // Extract cluster IDs from the clusters array in the response
          const clusterIds = versionData.clusters 
            ? versionData.clusters.map(clusterRelation => clusterRelation.clusterId)
            : [];
          
          // Update the form data with the fetched data
          setVersionModal(prev => ({
            ...prev,
            loading: false,
            formData: {
              description: versionData.description || '',
              weight: versionData.weight || '',
              competencyTemplateId: versionData.competencyTemplateId || '',
              type: versionData.type || '',
              templateVersionId: '', // This field is not editable in edit mode
              clusterIds: clusterIds
            }
          }));
        } catch (error) {
          showAlert('Failed to fetch version details', 'error', error);
          setVersionModal(prev => ({ ...prev, loading: false }));
        }
      };
      
      fetchVersionData();
    }
  }, [versionModal.isOpen, versionModal.mode, versionModal.version]);

  useEffect(() => {
    if (versionModal.formData.templateVersionId) {
      const selectedTemplate = templateVersions.find(
        template => template.id === versionModal.formData.templateVersionId
      );
      
      if (selectedTemplate && selectedTemplate.competencyTemplateId) {
        setVersionModal(prev => ({
          ...prev,
          formData: {
            ...prev.formData,
            competencyTemplateId: selectedTemplate.competencyTemplateId
          }
        }));
      }
    }
  }, [versionModal.formData.templateVersionId, templateVersions]);

  const fetchData = async () => {
    try {
      const [matrixData, versionsData] = await Promise.all([
        skillsApi.getSkillsMatrixById(matrixId),
        skillsApi.getTemplatesByMatrixId(matrixId)
      ]);
      setMatrix(matrixData);
      
      // Process the versions data to make clusters easier to access
      const processedVersions = versionsData.map(version => {
        // Add a convenience property for easy access to cluster IDs
        const clusterIds = version.clusters 
          ? version.clusters.map(clusterRelation => clusterRelation.clusterId)
          : [];
        
        return {
          ...version,
          clusterIds
        };
      });
      
      setVersions(processedVersions);
    } catch (error) {
      showAlert('Failed to fetch data', 'error', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompetencyTemplates = async () => {
    try {
      const templates = await competencyTemplatesApi.getAllTemplates();
      setCompetencyTemplates(templates);
    } catch (error) {
      showAlert('Failed to fetch competency templates', 'error', error);
    }
  };

  const fetchTemplateVersions = async () => {
    try {
      const templates = await skillsApi.getTemplatesByMatrixId(matrixId);
      setTemplateVersions(templates);
    } catch (error) {
      showAlert('Failed to fetch template versions', 'error', error);
    }
  };

  const fetchClusters = async () => {
    try {
      const clustersData = await organizationApi.getClusters();
      setClusters(clustersData || []);
    } catch (error) {
      showAlert('Failed to fetch clusters', 'error', error);
    }
  };

  // Helper function to manage version modal state
  const updateVersionModal = (config) => {
    const defaultFormData = {
      description: '',
      weight: '',
      type: '',
      competencyTemplateId: '',
      templateVersionId: '',
      clusterIds: []
    };

    const defaultConfig = {
      isOpen: false,
      mode: 'create',
      loading: false,
      version: null,
      formData: defaultFormData
    };

    setVersionModal({
      ...defaultConfig,
      ...config,
      formData: {
        ...defaultFormData,
        ...(config.formData || {})
      }
    });
  };

  const openCreateModal = () => {
    updateVersionModal({
      isOpen: true,
      mode: 'create'
    });
  };

  const openEditModal = (version) => {
    updateVersionModal({
      isOpen: true,
      mode: 'edit',
      loading: true,
      version
    });
  };

  const closeVersionModal = () => {
    updateVersionModal({
      isOpen: false
    });
  };

  const handleVersionFormChange = (field) => (event) => {
    const value = event?.target?.value ?? event;
    setVersionModal(prev => ({
      ...prev,
      formData: {
        ...prev.formData,
        [field]: value
      }
    }));
  };

  const handleVersionFormSubmit = async () => {
    try {
      setVersionModal(prev => ({ ...prev, loading: true }));
      
      // Prepare form data - make sure clusterIds is properly formatted
      const formData = {
        ...versionModal.formData,
        clusterIds: versionModal.formData.clusterIds || []
      };
      
      if (versionModal.mode === 'create') {
        await skillsApi.createSkillsMatrixTemplate(matrixId, formData);
        showAlert('Version created successfully', 'success');
      } else {
        await skillsApi.updateSkillsMatrixTemplate(versionModal.version.id, formData);
        showAlert('Version updated successfully', 'success');
      }
      
      closeVersionModal();
      fetchData();
      fetchTemplateVersions();
    } catch (error) {
      showAlert(error.message || `Failed to ${versionModal.mode} version`, 'error', error);
      setVersionModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleDeleteClick = (version) => {
    setDeleteModal({
      open: true,
      version
    });
  };

  const handleDelete = async () => {
    try {
      await skillsApi.deleteSkillsMatrixTemplate(deleteModal.version.id);
      showAlert('Version deleted successfully', 'success');
      setDeleteModal({ open: false, version: null });
      fetchData();
      fetchTemplateVersions();
    } catch (error) {
      showAlert(error.message || 'Failed to delete version', 'error', error);
    }
  };

  const handlePublishClick = (version) => {
    setPublishModal({
      open: true,
      version
    });
  };

  const handlePublish = async () => {
    try {
      await skillsApi.publishSkillsMatrixTemplate(publishModal.version.id);
      showAlert('Version published successfully', 'success');
      setPublishModal({ open: false, version: null });
      fetchData();
    } catch (error) {
      showAlert(error.message || 'Failed to publish version', 'error', error);
    }
  };

  const handleUnpublishClick = (version) => {
    setUnpublishModal({
      open: true,
      version
    });
  };

  const handleUnpublish = async () => {
    try {
      await skillsApi.unpublishSkillsMatrixTemplate(unpublishModal.version.id);
      showAlert('Version unpublished successfully', 'success');
      setUnpublishModal({ open: false, version: null });
      fetchData();
    } catch (error) {
      showAlert(error.message || 'Failed to unpublish version', 'error', error);
    }
  };

  const handleExportClick = async (version) => {
    setExportModal({
      open: true,
      version,
      loading: true,
      submitting: false,
      selectedTemplateId: null,
      allMatrices: []
    });

    try {
      // Fetch all available matrix templates for selection
      const allMatrices = await skillsApi.getAllSkillsMatrices();
      setExportModal(prev => ({
        ...prev,
        loading: false,
        allMatrices
      }));
    } catch (error) {
      showAlert('Failed to fetch matrix templates', 'error', error);
      setExportModal(prev => ({
        ...prev,
        loading: false
      }));
    }
  };

  const handleExport = async () => {
    if (!exportModal.selectedTemplateId || !exportModal.version) {
      showAlert('Please select a matrix template to export to', 'error');
      return;
    }

    try {
      setExportModal(prev => ({ ...prev, submitting: true }));
      
      // Call the export API with the version and selected matrix template
      await skillsApi.exportMatrixVersion(exportModal.version.id, exportModal.selectedTemplateId);
      
      showAlert('Version exported successfully', 'success');
      setExportModal({
        open: false,
        version: null,
        loading: false,
        submitting: false,
        selectedTemplateId: null,
        allMatrices: []
      });
    } catch (error) {
      showAlert(error.message || 'Failed to export version', 'error', error);
      setExportModal(prev => ({ ...prev, submitting: false }));
    }
  };

  const closeExportModal = () => {
    setExportModal({
      open: false,
      version: null,
      loading: false,
      submitting: false,
      selectedTemplateId: null,
      allMatrices: []
    });
  };

  const filteredVersions = versions.filter(version => 
    version.description?.toLowerCase().includes((searchTerm || '').toLowerCase())
  );

  // Selection mode functions
  const handleVersionToggle = (versionId) => {
    const newSelected = new Set(selectedVersions);
    if (newSelected.has(versionId)) {
      newSelected.delete(versionId);
    } else {
      newSelected.add(versionId);
    }
    setSelectedVersions(newSelected);
    setSelectAll(newSelected.size === filteredVersions.length && filteredVersions.length > 0);
  };

  const handleConfirmSelection = async () => {
    try {
      const selectedVersionsArray = Array.from(selectedVersions);
      if (onSelectionConfirm) {
        await onSelectionConfirm(selectedVersionsArray);
      }
      
      if (selectedVersions.size === 0 && initialSelectedVersions.length > 0) {
        showAlert('All versions removed successfully', 'success');
      } else {
        showAlert(`${selectedVersions.size} version(s) selected successfully`, 'success');
      }
      
      if (selectionBackPath) {
        navigate(selectionBackPath);
      }
    } catch (error) {
      showAlert('Failed to save selected versions', 'error', error);
    }
  };

  // Update selectAll when filtered versions change
  useEffect(() => {
    setSelectAll(selectedVersions.size === filteredVersions.length && filteredVersions.length > 0);
  }, [selectedVersions.size, filteredVersions.length]);

  if (loading) {
    return <LoadingSpinner />;
  }

  // Determine if form fields are valid for submission
  const isFormValid = () => {
    const { description, weight, type, competencyTemplateId } = versionModal.formData;
    return description.trim() && weight && type && competencyTemplateId;
  };

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate(
            isSelectionMode 
              ? (selectionBackPath || `/skills-matrix/sessions/${sessionId}`)
              : '/skills-matrix/templates'
          )}>
            <FaArrowLeft /> {isSelectionMode 
              ? (selectionBackText || 'Back to Session') 
              : 'Back to Skills Matrix Templates'}
          </BackButton>
          <Title>
            {isSelectionMode 
              ? (selectionTitle || `${matrix?.name} | Select Versions`)
              : `${matrix?.name} | Versions`}
          </Title>
        </HeaderLeft>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <HeaderButton onClick={() => setShowControls(!showControls)}>
            {showControls ? <FaEyeSlash /> : <FaEye />}
            {showControls ? 'Hide Panels' : 'Show Panels'}
          </HeaderButton>
        </Box>
      </Header>

      <Content>
        <ControlsContainer $show={showControls} style={{ marginBottom: '20px' }}>
          <Box sx={{ 
            display: 'flex', 
            gap: '16px', 
            flexWrap: 'wrap',
            width: '100%',
            alignItems: 'flex-start',
            justifyContent: 'space-between'
          }}>
            <TextField
              placeholder="Search Versions"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoComplete="off"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FaSearch style={{ color: '#6B7280' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: 1,
                maxWidth: '500px',
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  height: '40px',
                  fontSize: '14px',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#2563eb',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#2563eb',
                  }
                },
                '& .MuiInputBase-input': {
                  padding: '8px 14px',
                }
              }}
            />

            {!isSelectionMode && (
              <HeaderButton 
                onClick={openCreateModal}
                sx={{
                  minWidth: 'auto',
                  height: '32px',
                  padding: '0 16px',
                  backgroundColor: '#2563eb',
                  color: 'white',
                  alignSelf: 'flex-start',
                  '&:hover': {
                    backgroundColor: '#1d4ed8',
                  }
                }}
              >
                <FaPlus style={{ marginRight: '8px' }} /> Create Version
              </HeaderButton>
            )}
          </Box>
        </ControlsContainer>

        <MainContainer show={showControls}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 2
          }}>
            <Typography 
              sx={{ 
                color: '#64748B',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <Box component="span" sx={{ fontWeight: 600 }}>
                {filteredVersions.length}
              </Box>
              {filteredVersions.length === 1 ? 'version' : 'versions'} found
            </Typography>
            
            {isSelectionMode && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {selectedVersions.size > 0 ? (
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      backgroundColor: '#F0F7FF',
                      border: '1px solid #DBEAFE',
                      borderRadius: '6px',
                      padding: '4px 12px',
                      gap: 1
                    }}>
                      <Badge 
                        badgeContent={selectedVersions.size} 
                        color="primary"
                        sx={{ 
                          '& .MuiBadge-badge': { 
                            backgroundColor: '#3B82F6',
                            color: 'white',
                            fontWeight: 600
                          } 
                        }}
                      >
                        <Typography sx={{ color: '#4B83F0', fontSize: '14px', fontWeight: 500 }}>
                          Selected
                        </Typography>
                      </Badge>
                      
                      <Box sx={{ 
                        height: '20px', 
                        borderLeft: '1px solid #DBEAFE',
                        mx: 1
                      }} />
                      
                      <Button
                        size="small"
                        onClick={() => setSelectedVersions(new Set())}
                        sx={{
                          color: '#4B83F0',
                          fontSize: '14px',
                          textTransform: 'none',
                          minWidth: 'auto',
                          padding: '0',
                          '&:hover': {
                            backgroundColor: 'transparent',
                            textDecoration: 'underline'
                          }
                        }}
                        startIcon={<FaTimes size={12} />}
                      >
                        Clear
                      </Button>
                    </Box>
                  ) : (
                    <Typography sx={{ color: '#64748B', fontSize: '14px', fontStyle: 'italic' }}>
                      {initialSelectedVersions.length > 0 
                        ? 'All versions deselected - confirm to remove all' 
                        : 'Click on versions to select them'}
                    </Typography>
                  )}
                  
                  {(selectedVersions.size > 0 || (selectedVersions.size === 0 && initialSelectedVersions.length > 0)) && (
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={handleConfirmSelection}
                      startIcon={<FaCheck />}
                      sx={{
                        backgroundColor: selectedVersions.size === 0 && initialSelectedVersions.length > 0 ? '#DC2626' : '#3B82F6',
                        textTransform: 'none',
                        fontWeight: 500,
                        '&:hover': {
                          backgroundColor: selectedVersions.size === 0 && initialSelectedVersions.length > 0 ? '#B91C1C' : '#2563EB'
                        }
                      }}
                    >
                      {selectedVersions.size === 0 && initialSelectedVersions.length > 0 
                        ? 'Remove All' 
                        : 'Confirm'}
                    </Button>
                  )}
                </Box>
              </Box>
            )}
          </Box>

          {filteredVersions.length === 0 ? (
            <EmptyState
              title="No versions found"
              description={searchTerm ? 
                "No versions match your search criteria. Try adjusting your search term." : 
                "No versions have been created yet. Click the '+ Create Version' button to create your first version."}
            />
          ) : (
            <MatrixGrid>
              {filteredVersions.map((version) => (
                <MatrixCard 
                  key={version.id}
                  onClick={isSelectionMode ? () => handleVersionToggle(version.id) : undefined}
                  style={isSelectionMode ? {
                    cursor: 'pointer',
                    border: selectedVersions.has(version.id) ? '1px solid #93C5FD' : '1px solid #E5E7EB',
                    backgroundColor: selectedVersions.has(version.id) ? '#EFF6FF' : '#f8f9fa',
                    transition: 'all 0.2s ease',
                    boxShadow: selectedVersions.has(version.id) ? '0 2px 8px rgba(59, 130, 246, 0.15)' : 'none',
                    position: 'relative'
                  } : {}}
                  onMouseEnter={isSelectionMode ? (e) => {
                    if (!selectedVersions.has(version.id)) {
                      e.currentTarget.style.backgroundColor = '#f1f3f5';
                      e.currentTarget.style.borderColor = '#bdbdbd';
                    } else {
                      e.currentTarget.style.backgroundColor = '#DBEAFE';
                      e.currentTarget.style.borderColor = '#60A5FA';
                    }
                  } : undefined}
                  onMouseLeave={isSelectionMode ? (e) => {
                    if (!selectedVersions.has(version.id)) {
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                      e.currentTarget.style.borderColor = '#E5E7EB';
                    } else {
                      e.currentTarget.style.backgroundColor = '#EFF6FF';
                      e.currentTarget.style.borderColor = '#93C5FD';
                    }
                  } : undefined}
                >
                  {isSelectionMode && (
                    <Box sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      backgroundColor: selectedVersions.has(version.id) ? '#3B82F6' : 'white',
                      border: `2px solid ${selectedVersions.has(version.id) ? '#3B82F6' : '#CBD5E1'}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 1
                    }}>
                      {selectedVersions.has(version.id) && <FaCheck size={12} color="#fff" />}
                    </Box>
                  )}
                  <MatrixCardContent>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      mb: 2,
                      gap: 1,
                      paddingRight: isSelectionMode ? '28px' : '0'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={getTemplateTypeText(version.type)}
                          sx={{ 
                            backgroundColor: getTemplateTypeColor(version.type),
                            color: 'white',
                            fontWeight: 500,
                            fontSize: '13px',
                            height: '24px',
                            minWidth: 'fit-content',
                            '& .MuiChip-label': {
                              padding: '0 8px',
                              whiteSpace: 'nowrap'
                            }
                          }}
                        />
                        <Typography variant="h6" sx={{ 
                          color: COLORS.PRIMARY, 
                          fontSize: '14px',
                          fontWeight: 600,
                          minWidth: 'fit-content'
                        }}>
                          v.{version.version}
                        </Typography>
                      </Box>
                      <Chip
                        label={getStatusText(version.publishedAt ? MATRIX_TEMPLATE_STATUS.PUBLISHED : MATRIX_TEMPLATE_STATUS.DRAFT)}
                        size="small"
                        sx={{ 
                          backgroundColor: getStatusColor(version.publishedAt ? MATRIX_TEMPLATE_STATUS.PUBLISHED : MATRIX_TEMPLATE_STATUS.DRAFT),
                          color: getStatusTextColor(version.publishedAt ? MATRIX_TEMPLATE_STATUS.PUBLISHED : MATRIX_TEMPLATE_STATUS.DRAFT),
                          border: '1px solid',
                          borderColor: `${getStatusTextColor(version.publishedAt ? MATRIX_TEMPLATE_STATUS.PUBLISHED : MATRIX_TEMPLATE_STATUS.DRAFT)}20`,
                          height: '24px',
                          minWidth: 'fit-content',
                          '& .MuiChip-label': {
                            fontSize: '13px',
                            padding: '0 8px',
                            fontWeight: 500,
                            whiteSpace: 'nowrap'
                          }
                        }}
                      />
                    </Box>
                    {/* Display attached clusters or global availability message */}
                    <Box sx={{ mb: 1, display: 'flex', flexDirection: 'column' }}>
                      {version.clusters && version.clusters.length > 0 ? (
                        <>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Available in:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {version.clusters.map(clusterRelation => {
                              const cluster = clusters.find(c => c.id === clusterRelation.clusterId);
                              return cluster ? (
                                <Chip
                                  key={cluster.id}
                                  /* ADR-119: NAME · CODE, as ClusterSelector renders
                                     a selected cluster; full type name on hover. */
                                  label={cluster.orgStructureType?.code
                                    ? `${cluster.name} · ${cluster.orgStructureType.code}`
                                    : cluster.name}
                                  title={cluster.orgStructureType?.name || undefined}
                                  size="small"
                                  sx={getClusterChipStyle()}
                                />
                              ) : null;
                            })}
                          </Box>
                        </>
                      ) : (
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontStyle: 'italic', 
                            color: COLORS.GRAY 
                          }}
                        >
                          Available globally
                        </Typography>
                      )}
                    </Box>
                    
                    <MatrixDetailsGrid>
                      {version.description ? (
                        <Typography color={COLORS.PRIMARY}>
                          {version.description}
                        </Typography>
                      ) : (
                        <ItalicText>No description provided</ItalicText>
                      )}
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Created: {new Date(version.createdAt).toLocaleDateString()}
                        </Typography>
                        {version.publishedAt && (
                          <Typography variant="body2" color="text.secondary">
                            Published: {toLocalDate(version.publishedAt)?.toLocaleDateString()}
                          </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Weight: {version.weight}
                        </Typography>
                        {version.competencyTemplate && (
                          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Using:
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {version.competencyTemplate.name}
                              </Typography>
                              <Chip
                                label={getSkillTypeText(version.competencyTemplate.type)}
                                size="small"
                                sx={{ 
                                  backgroundColor: getSkillTypeColor(version.competencyTemplate.type),
                                  color: getSkillTypeTextColor(version.competencyTemplate.type),
                                  height: '16px',
                                  fontSize: '0.65rem',
                                  fontWeight: 500,
                                  '& .MuiChip-label': {
                                    padding: '0 4px',
                                  }
                                }}
                              />
                            </Box>
                          </Box>
                        )}
                        <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic', color: COLORS.GRAY }}>
                          {version.skillsTemplates?.length > 0 ? 
                            `${version.skillsTemplates.length} ${version.skillsTemplates.length === 1 ? 'skill' : 'skills'} attached` : 
                            'No skills attached'}
                        </Typography>
                      </Box>
                    </MatrixDetailsGrid>
                    {!isSelectionMode && (
                      <ActionButtons>
                        {!version.publishedAt && (
                          <IconButton
                            onClick={() => handlePublishClick(version)}
                            color="primary"
                            size="small"
                            title="Publish Version"
                          >
                            <FaCloudUploadAlt />
                          </IconButton>
                        )}
                        {version.publishedAt && (
                          <IconButton
                            onClick={() => handleUnpublishClick(version)}
                            color="primary"
                            size="small"
                            title="Unpublish Version"
                          >
                            <FaCloudDownloadAlt />
                          </IconButton>
                        )}
                        <IconButton
                          onClick={() => openEditModal(version)}
                          color="primary"
                          size="small"
                          title="Edit Version"
                        >
                          <FaEdit />
                        </IconButton>
                        <IconButton
                          onClick={() => navigate(`/skills-matrix/templates/${matrixId}/versions/${version.id}/configure`)}
                          color="primary"
                          size="small"
                          title="Configure Version"
                        >
                          <FaCogs />
                        </IconButton>
                        <IconButton
                          onClick={() => handleExportClick(version)}
                          color="primary"
                          size="small"
                          title="Export Version"
                        >
                          <FaFileExport />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDeleteClick(version)}
                          color="error"
                          size="small"
                          title="Delete Version"
                        >
                          <FaTrash />
                        </IconButton>
                      </ActionButtons>
                    )}
                  </MatrixCardContent>
                </MatrixCard>
              ))}
            </MatrixGrid>
          )}
        </MainContainer>
      </Content>

      {/* Unified Version Modal (Create/Edit) - Only in normal mode */}
      {!isSelectionMode && (
        <Modal
          isOpen={versionModal.isOpen}
          onClose={closeVersionModal}
          title={versionModal.mode === 'create' ? 'Create New Version' : 'Edit Version'}
          onSubmit={handleVersionFormSubmit}
          submitText={versionModal.mode === 'create' ? 'Create Version' : 'Save Changes'}
          cancelText="Cancel"
          submitDisabled={!isFormValid()}
        >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 2 }}>
          {versionModal.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <LoadingSpinner size={40} />
            </Box>
          ) : (
            <>
              <FormControl fullWidth disabled={versionModal.mode === 'edit'}>
                <InputLabel>Template Type *</InputLabel>
                <Select
                  value={versionModal.formData.type}
                  onChange={handleVersionFormChange('type')}
                  label="Template Type"
                  renderValue={(selected) => {
                    if (!selected) {
                      return <em>Select a template type</em>;
                    }
                    return getTemplateTypeText(selected);
                  }}
                >
                  {Object.entries(MATRIX_TEMPLATE_TYPES).map(([key, value]) => (
                    <MenuItem 
                      key={key} 
                      value={value}
                      sx={{
                        color: getTemplateTypeTextColor(value),
                        '&.Mui-selected': {
                          backgroundColor: `${getTemplateTypeColor(value)}20`,
                        },
                        '&:hover': {
                          backgroundColor: `${getTemplateTypeColor(value)}10`,
                        }
                      }}
                    >
                      {getTemplateTypeText(value)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {versionModal.mode === 'create' && (
                <FormControl fullWidth>
                  <InputLabel>Template Version</InputLabel>
                  <Select
                    value={versionModal.formData.templateVersionId}
                    onChange={handleVersionFormChange('templateVersionId')}
                    label="Template Version"
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {templateVersions.map((template) => (
                      <MenuItem 
                        key={template.id} 
                        value={template.id}
                        sx={{
                          '&.Mui-selected': {
                            backgroundColor: `${getTemplateTypeColor(template.type)}20`,
                          },
                          '&:hover': {
                            backgroundColor: `${getTemplateTypeColor(template.type)}10`,
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography>{template.description || 'Unnamed template'}</Typography>
                          <Typography variant="body2" sx={{ 
                            color: COLORS.PRIMARY, 
                            fontSize: '12px',
                            fontWeight: 600,
                          }}>
                            v.{template.version}
                          </Typography>
                          <Chip
                            label={getTemplateTypeText(template.type)}
                            size="small"
                            sx={{ 
                              backgroundColor: getTemplateTypeColor(template.type),
                              color: 'white',
                              height: '20px',
                              fontSize: '0.7rem',
                              fontWeight: 500
                            }}
                          />
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <FormControl fullWidth disabled={versionModal.mode === 'edit' || !!versionModal.formData.templateVersionId}>
                <InputLabel>Competency Template *</InputLabel>
                <Select
                  value={versionModal.formData.competencyTemplateId}
                  onChange={handleVersionFormChange('competencyTemplateId')}
                  label="Competency Template"
                  renderValue={(selected) => {
                    if (!selected) {
                      return <em>Select a competency template</em>;
                    }
                    const template = competencyTemplates.find(t => t.id === selected);
                    if (!template) return selected;
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography>{template.name}</Typography>
                        <Chip
                          label={getSkillTypeText(template.type)}
                          size="small"
                          sx={{ 
                            backgroundColor: getSkillTypeColor(template.type),
                            color: getSkillTypeTextColor(template.type),
                            height: '20px',
                            fontSize: '0.7rem',
                            fontWeight: 500
                          }}
                        />
                      </Box>
                    );
                  }}
                >
                  {competencyTemplates.map((template) => (
                    <MenuItem 
                      key={template.id} 
                      value={template.id}
                      sx={{
                        '&.Mui-selected': {
                          backgroundColor: `${getSkillTypeColor(template.type)}20`,
                        },
                        '&:hover': {
                          backgroundColor: `${getSkillTypeColor(template.type)}10`,
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography>{template.name}</Typography>
                        <Chip
                          label={getSkillTypeText(template.type)}
                          size="small"
                          sx={{ 
                            backgroundColor: getSkillTypeColor(template.type),
                            color: getSkillTypeTextColor(template.type),
                            height: '20px',
                            fontSize: '0.7rem',
                            fontWeight: 500
                          }}
                        />
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Weight *"
                type="number"
                value={versionModal.formData.weight}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || (value >= 0 && value <= 10)) {
                    handleVersionFormChange('weight')({
                      target: { value }
                    });
                  }
                }}
                inputProps={{ 
                  step: "0.1",
                  min: "0",
                  max: "10"
                }}
                fullWidth
                helperText="Enter a value between 0 and 10"
              />

              <TextField
                label="Description *"
                multiline
                rows={4}
                fullWidth
                value={versionModal.formData.description}
                onChange={handleVersionFormChange('description')}
                placeholder="Enter version description"
                helperText={!versionModal.formData.description.trim() ? "Description is required" : ""}
              />
              
              <ClusterSelector
                multiple
                leafOnly
                value={versionModal.formData.clusterIds}
                onChange={(selectedIds) => {
                  handleVersionFormChange('clusterIds')({
                    target: { value: selectedIds }
                  });
                }}
                label="Attached To"
                placeholder="Select clusters (optional)"
                showStructureType // ADR-119
              />
            </>
          )}
        </Box>
      </Modal>
      )}

            {/* Other modals - Delete, Publish, Unpublish - Only in normal mode */}
      {!isSelectionMode && (
        <>
          <ConfirmationModal
            isOpen={deleteModal.open}
            onClose={() => setDeleteModal({ open: false, version: null })}
            onConfirm={handleDelete}
            title="Delete Version"
            message={`Are you sure you want to delete version ${deleteModal.version?.version}? This action cannot be undone.`}
          />

          <ConfirmationModal
            isOpen={publishModal.open}
            onClose={() => setPublishModal({ open: false, version: null })}
            onConfirm={handlePublish}
            title="Publish Version"
            message={`Are you sure you want to publish version ${publishModal.version?.version}? This action cannot be undone.`}
            actionButtonText="Publish"
          />

          <ConfirmationModal
            isOpen={unpublishModal.open}
            onClose={() => setUnpublishModal({ open: false, version: null })}
            onConfirm={handleUnpublish}
            title="Unpublish Version"
            message={`Are you sure you want to unpublish version ${unpublishModal.version?.version}? This action cannot be undone.`}
            actionButtonText="Unpublish"
          />

          {/* Export Modal */}
          <Modal
            isOpen={exportModal.open}
            onClose={closeExportModal}
            title={`Export ${exportModal.version?.description} v.${exportModal.version?.version}`}
            onSubmit={handleExport}
            submitText="Export"
            cancelText="Cancel"
            submitDisabled={!exportModal.selectedTemplateId || exportModal.submitting}
            maxWidth="md"
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
              {exportModal.loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <LoadingSpinner size={40} />
                </Box>
              ) : (
                <>
                  <Typography variant="body1" sx={{ mb: 0 }}>
                    Select a matrix template to export this version to:
                  </Typography>
                  
                  <Typography 
                    sx={{ 
                      color: '#64748B',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      mb: 0
                    }}
                  >
                    <Box component="span" sx={{ fontWeight: 600 }}>
                      {exportModal.allMatrices.length}
                    </Box>
                    {exportModal.allMatrices.length === 1 ? 'template' : 'templates'} available
                  </Typography>

                   {exportModal.allMatrices.length === 0 ? (
                     <EmptyState
                       title="No matrix templates available"
                       description="There are no matrix templates available for export. Create a matrix template first to enable exports."
                     />
                   ) : (
                    <Box sx={{ 
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      maxHeight: '400px',
                      overflow: 'auto'
                    }}>
                      <List dense>
                        {exportModal.allMatrices.map((matrixTemplate) => (
                          <ListItem 
                             key={matrixTemplate.id}
                             button
                             onClick={() => setExportModal(prev => ({
                               ...prev,
                               selectedTemplateId: prev.selectedTemplateId === matrixTemplate.id ? null : matrixTemplate.id
                             }))}
                             secondaryAction={
                               <Radio
                                 checked={exportModal.selectedTemplateId === matrixTemplate.id}
                                 color="primary"
                               />
                             }
                             sx={{
                               borderBottom: '1px solid #F3F4F6',
                               '&:last-child': {
                                 borderBottom: 'none'
                               },
                               backgroundColor: exportModal.selectedTemplateId === matrixTemplate.id ? '#F0F7FF' : 'transparent',
                               '&:hover': {
                                 backgroundColor: exportModal.selectedTemplateId === matrixTemplate.id ? '#E0F2FE' : '#F8FAFC'
                               }
                             }}
                           >
                             <ListItemText
                               primary={
                                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                   <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                     {matrixTemplate.name}
                                   </Typography>
                                 </Box>
                               }
                               secondary={
                                 <Box component="div" sx={{ mt: 1 }}>
                                   <Typography variant="body2" color="text.secondary" component="div">
                                     Position: {matrixTemplate.position?.label}
                                   </Typography>
                                   {matrixTemplate.matrixRoles && matrixTemplate.matrixRoles.length > 0 && (
                                     <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                       {matrixTemplate.matrixRoles.map(mr => (
                                         <Chip 
                                           key={mr.id} 
                                           label={mr.role?.label} 
                                           size="small"
                                           sx={{
                                             height: '20px',
                                             fontSize: '0.7rem',
                                             backgroundColor: '#E3F2FD',
                                             color: '#1976D2'
                                           }}
                                         />
                                       ))}
                                     </Box>
                                   )}
                                 </Box>
                               }
                             />
                           </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Modal>
        </>
      )}
    </Container>
  );
};

export default SkillsMatrixVersions; 